import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import gsap from "utils/gsap";

import Overview from "./Overview/Overview";
import Operations from "./Operations";
import Customers from "./Customers";
import Financial from "./Financial";

import PageHeader from "components/common/PageHeader";
import {
  getDashboardData,
  getDashboardRecentActivity,
} from "store/slices/dashboardSlice";
import { DASHBOARD_TABS, TAB_LIST } from "utils/dashboardUtils";
import DashboardFilter from "./DashboardFilter";
import { getMinMaxDate } from "utils/utils";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getBranchesList } from "store/slices/branchesSlice";

const COMPONENT_MAP = {
  Overview,
  Operations,
  Customers,
  Financial,
};



const Dashboard = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const tabsRef = useRef(null);

  const [activeTabId, setActiveTabId] = useState(DASHBOARD_TABS.OVERVIEW.id);
  const { itemsList: branchesList } = useSelector((state) => state.Branches);

  const getTodayRange = () => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);

    const to = new Date();

    return [from, to];
  };
  const [dates, setDates] = useState(getTodayRange);
  const [dashboardFilters, setDashboardFilters] = useState({
    branches: [],
  });
  useEffect(() => {
    dispatch(getBranchesList());
  }, []);
  const dashboardFiltersList = useMemo(
    () => [
      {
        key: "branches",
        placeholder: t("Select Store"),
        options: formatOptionsForSelect(branchesList, language, "name"),
      },
    ],
    [t, branchesList, language],
  );

  const activeTab = TAB_LIST.find((tab) => tab.id === activeTabId);
  const ActiveComponent = COMPONENT_MAP[activeTab.component];

  /* ------------------ Animations ------------------ */
  useEffect(() => {
    if (!tabsRef.current) return;

    gsap.fromTo(
      tabsRef.current.querySelectorAll(".tabs_inner"),
      { opacity: 0, y: 8 },
      {
        opacity: 1,
        y: 0,
        duration: 0.25,
        stagger: 0.05,
        ease: "power2.out",
      },
    );
  }, []);

  /* ------------------ API Calls ------------------ */
  const buildDashboardPayload = (subType = null) => {
    const payload = {
      dateRange: getMinMaxDate(dates),
      timezone: "Asia/Riyadh",
    };

    if (activeTab?.key === DASHBOARD_TABS.OVERVIEW.key) {
      payload.branches = dashboardFilters.branches || [];
    }

    if (subType) {
      payload.subType = subType;
    }

    return payload;
  };

  useEffect(() => {
    if (!activeTab) return;

    activeTab.subTypes.forEach((subType) => {
      dispatch(
        getDashboardData({
          type: activeTab.key,
          payload: buildDashboardPayload(subType),
        }),
      );
    });

    if (activeTab.extraApi === "recentActivity") {
      dispatch(
        getDashboardRecentActivity({
          type: activeTab.key,
          payload: buildDashboardPayload(),
        }),
      );
    }
  }, [activeTab, dates, dashboardFilters, dispatch]);

  /* ------------------ Render ------------------ */
  return (
    <div className="page-content dashboard_page">
      <div className="d-flex justify-content-between top_wrapper">
        <PageHeader
          title={t("Dashboard")}
          subtitle={t("Monitor performance and take action instantly")}
        />
        <DashboardFilter
          dates={dates}
          setDates={setDates}
          filters={dashboardFiltersList}
          selectedFilters={dashboardFilters}
          setSelectedFilters={setDashboardFilters}
          activeTabKey={activeTab?.key} // Pass the active tab key
        />{" "}
      </div>

      {/* Tabs */}
      <div className="tabs" ref={tabsRef}>
        {TAB_LIST.map((tab) => (
          <div className="tabs_inner" key={tab.id}>
            <button
              className={activeTabId === tab.id ? "active" : ""}
              onClick={() => setActiveTabId(tab.id)}
            >
              {t(tab.label)}
            </button>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="dashboard_inner_section">
        <ActiveComponent />
      </div>
    </div>
  );
};

export default Dashboard;
